class SocialNetworkTicker extends HTMLElement {
  constructor() {
    super()

    this.props = {
      foursixtyAPI: 'https://foursixty.com/api/v2/aligne/timeline?use_stored_image_url=true&page_size=10',
      backend: this.dataset.backend || 'foursixty',
    }

    this.state = {
      view: this.props.backend,
      fourSixtyPosts: [],
      productImages: new Map()
    }

    this.els = {
      slides: this.querySelector('[data-social-network-ticker-el="slides"]'),
      slide: this.querySelectorAll('[data-social-network-ticker-el="slide"]'),
    }

    this.externalEls = {
      wrapper: document.querySelector('[data-social-network-ticker-external-el="wrapper"]'),
    }
  }

  connectedCallback () {
    if (this.state.view === 'foursixty') {
      this.fetchAPI()
        .then(() => this.fetchProductImages(this.state.fourSixtyPosts))
        .then(() => {
          this.buildSlides()
          this.fallbackToHiddenIfImages404()
          this.setupCarousel()
        })
        .catch(() => {
          const section = this.closest('section')
          if (section) section.style.display = 'none'
        })
    } else {
      this.setupCarousel()
    }
  }

  async fetchAPI () {
    try {
      const response = await fetch(this.props.foursixtyAPI)

      if (!response.ok) {
        throw new Error(`FourSixty API responded with status ${response.status}`)
      }

      const data = await response.json()
      this.state.fourSixtyPosts = data.results
      return data
    } catch (error) {
      console.error('Error fetching data from Foursixty API:', error)
      throw error
    }
  }

  async fetchProductImages (posts) {
    const postsWithLinks = posts.filter(post => post.links.length > 0)

    const handles = [...new Set(
      postsWithLinks.flatMap(post =>
        post.links.map(link => link.product.url.split('/products/').pop())
      )
    )]

    await Promise.all(handles.map(async handle => {
      try {
        const response = await fetch(`/products/${handle}.json?fields=images`)
        if (!response.ok) return
        const data = await response.json()
        this.state.productImages.set(handle, data.product.images)
      } catch {
        // silently skip — productTemplate will fall back to FourSixty image
      }
    }))
  }

  buildSlides () {
    const skeletons = Array.from(this.els.slides.querySelectorAll('.sec-SocialNetwork_SlideSkeleton'))

    this.state.fourSixtyPosts.forEach((post, index) => {
      const temp = document.createElement('div')

      if(post.links.length > 0) {
        temp.innerHTML = this.productTemplate(post).trim()
      } else {
        temp.innerHTML = this.postTemplate(post).trim()
      }

      const slideEl = temp.firstElementChild

      if (skeletons[index]) {
        skeletons[index].replaceWith(slideEl)
      } else {
        this.els.slides.appendChild(slideEl)
      }
    })

    skeletons.slice(this.state.fourSixtyPosts.length).forEach(skeleton => skeleton.remove())
  }

  fallbackToHiddenIfImages404 () {
    this.els.slides.querySelectorAll('img').forEach(img => {
      img.addEventListener('error', () => {
        this.externalEls.wrapper.style.display = 'none'
    });
    })
  }

  setupCarousel () {
    const autoplaySpeed = this.dataset.autoplaySpeed ? parseInt(this.dataset.autoplaySpeed) : 5000

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches

    if (prefersReducedMotion) {
      this.els.slides.setAttribute('data-reduced-motion', 'true')
      return
    }

    const slidesArray = Array.from(this.querySelector('[data-social-network-ticker-el="slides"]').children)
    const numberOfSlides = slidesArray.length

    slidesArray.forEach(slide => {
      const clone = slide.cloneNode(true)
      this.els.slides.appendChild(clone)
    })

    const initAnimation = () => {
      const totalWidth = this.els.slides.scrollWidth
      const scrollDistance = totalWidth / 2

      const animationDuration = autoplaySpeed * numberOfSlides

      this.els.slides.style.setProperty('--animation-duration', `${animationDuration}ms`)
      this.els.slides.style.setProperty('--scroll-distance', `-${scrollDistance}px`)
      this.els.slides.setAttribute('data-initialised', 'true')
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(initAnimation, 100)
      })
    })

    this.addEventListener('mouseenter', () => {
      this.els.slides.style.animationPlayState = 'paused'
    })

    this.addEventListener('mouseleave', () => {
      this.els.slides.style.animationPlayState = 'running'
    })
  }

  postTemplate (post) {
    return `
      <div
        class="sec-SocialNetwork_Slide"
        data-social-network-ticker-el="slide"
      >
        <a
          href="${post.resource_url}"
          target="_blank"
          rel="noopener"
        >
          <div class="sec-SocialNetwork_ImageContainer">
            <div class="sec-SocialNetwork_Image">
              <picture>
                <img src="${post.main_image_url}" alt="${post.title}" loading="lazy">
              </picture>
            </div>

            <div class="sec-SocialNetwork_Icon">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_1811_399)">
                  <path d="M18 3.24141C22.8094 3.24141 23.3789 3.2625 25.2703 3.34687C27.0281 3.42422 27.9773 3.71953 28.6102 3.96563C29.4469 4.28906 30.0516 4.68281 30.6773 5.30859C31.3102 5.94141 31.6969 6.53906 32.0203 7.37578C32.2664 8.00859 32.5617 8.96484 32.6391 10.7156C32.7234 12.6141 32.7445 13.1836 32.7445 17.9859C32.7445 22.7953 32.7234 23.3648 32.6391 25.2563C32.5617 27.0141 32.2664 27.9633 32.0203 28.5961C31.6969 29.4328 31.3031 30.0375 30.6773 30.6633C30.0445 31.2961 29.4469 31.6828 28.6102 32.0062C27.9773 32.2523 27.0211 32.5477 25.2703 32.625C23.3719 32.7094 22.8023 32.7305 18 32.7305C13.1906 32.7305 12.6211 32.7094 10.7297 32.625C8.97188 32.5477 8.02266 32.2523 7.38984 32.0062C6.55313 31.6828 5.94844 31.2891 5.32266 30.6633C4.68984 30.0305 4.30312 29.4328 3.97969 28.5961C3.73359 27.9633 3.43828 27.007 3.36094 25.2563C3.27656 23.3578 3.25547 22.7883 3.25547 17.9859C3.25547 13.1766 3.27656 12.607 3.36094 10.7156C3.43828 8.95781 3.73359 8.00859 3.97969 7.37578C4.30312 6.53906 4.69688 5.93437 5.32266 5.30859C5.95547 4.67578 6.55313 4.28906 7.38984 3.96563C8.02266 3.71953 8.97891 3.42422 10.7297 3.34687C12.6211 3.2625 13.1906 3.24141 18 3.24141ZM18 0C13.1133 0 12.5016 0.0210937 10.582 0.105469C8.66953 0.189844 7.35469 0.499219 6.21563 0.942187C5.02734 1.40625 4.02188 2.01797 3.02344 3.02344C2.01797 4.02187 1.40625 5.02734 0.942188 6.20859C0.499219 7.35469 0.189844 8.6625 0.105469 10.575C0.0210938 12.5016 0 13.1133 0 18C0 22.8867 0.0210938 23.4984 0.105469 25.418C0.189844 27.3305 0.499219 28.6453 0.942188 29.7844C1.40625 30.9727 2.01797 31.9781 3.02344 32.9766C4.02188 33.975 5.02734 34.5937 6.20859 35.0508C7.35469 35.4937 8.6625 35.8031 10.575 35.8875C12.4945 35.9719 13.1062 35.993 17.993 35.993C22.8797 35.993 23.4914 35.9719 25.4109 35.8875C27.3234 35.8031 28.6383 35.4937 29.7773 35.0508C30.9586 34.5937 31.9641 33.975 32.9625 32.9766C33.9609 31.9781 34.5797 30.9727 35.0367 29.7914C35.4797 28.6453 35.7891 27.3375 35.8734 25.425C35.9578 23.5055 35.9789 22.8937 35.9789 18.007C35.9789 13.1203 35.9578 12.5086 35.8734 10.5891C35.7891 8.67656 35.4797 7.36172 35.0367 6.22266C34.5938 5.02734 33.982 4.02187 32.9766 3.02344C31.9781 2.025 30.9727 1.40625 29.7914 0.949219C28.6453 0.50625 27.3375 0.196875 25.425 0.1125C23.4984 0.0210938 22.8867 0 18 0Z" fill="white"/>
                  <path d="M18 8.75391C12.8953 8.75391 8.75391 12.8953 8.75391 18C8.75391 23.1047 12.8953 27.2461 18 27.2461C23.1047 27.2461 27.2461 23.1047 27.2461 18C27.2461 12.8953 23.1047 8.75391 18 8.75391ZM18 23.9977C14.6883 23.9977 12.0023 21.3117 12.0023 18C12.0023 14.6883 14.6883 12.0023 18 12.0023C21.3117 12.0023 23.9977 14.6883 23.9977 18C23.9977 21.3117 21.3117 23.9977 18 23.9977Z" fill="white"/>
                  <path d="M29.7703 8.38821C29.7703 9.58352 28.8 10.5468 27.6117 10.5468C26.4164 10.5468 25.4531 9.57649 25.4531 8.38821C25.4531 7.1929 26.4234 6.22961 27.6117 6.22961C28.8 6.22961 29.7703 7.19993 29.7703 8.38821Z" fill="white"/>
                  </g>
                  <defs>
                  <clipPath id="clip0_1811_399">
                  <rect width="36" height="36" fill="white"/>
                  </clipPath>
                  </defs>
                </svg>
            </div>
          </div>
        </a>
      </div>
    `
  }

  productTemplate (post) {
    const products = post.links

    return `
      <div class="sec-SocialNetwork_Slide" data-social-network-ticker-el="slide">
        <a href="${post.resource_url}" target="_blank">
          <div class="sec-SocialNetwork_ImageContainer">
            <div class="sec-SocialNetwork_Image">
              <picture>
                <img src="${post.main_image_url}" alt="${post.title}" loading="lazy">
              </picture>
            </div>

            <div class="sec-SocialNetwork_Icon">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_1811_399)">
                  <path d="M18 3.24141C22.8094 3.24141 23.3789 3.2625 25.2703 3.34687C27.0281 3.42422 27.9773 3.71953 28.6102 3.96563C29.4469 4.28906 30.0516 4.68281 30.6773 5.30859C31.3102 5.94141 31.6969 6.53906 32.0203 7.37578C32.2664 8.00859 32.5617 8.96484 32.6391 10.7156C32.7234 12.6141 32.7445 13.1836 32.7445 17.9859C32.7445 22.7953 32.7234 23.3648 32.6391 25.2563C32.5617 27.0141 32.2664 27.9633 32.0203 28.5961C31.6969 29.4328 31.3031 30.0375 30.6773 30.6633C30.0445 31.2961 29.4469 31.6828 28.6102 32.0062C27.9773 32.2523 27.0211 32.5477 25.2703 32.625C23.3719 32.7094 22.8023 32.7305 18 32.7305C13.1906 32.7305 12.6211 32.7094 10.7297 32.625C8.97188 32.5477 8.02266 32.2523 7.38984 32.0062C6.55313 31.6828 5.94844 31.2891 5.32266 30.6633C4.68984 30.0305 4.30312 29.4328 3.97969 28.5961C3.73359 27.9633 3.43828 27.007 3.36094 25.2563C3.27656 23.3578 3.25547 22.7883 3.25547 17.9859C3.25547 13.1766 3.27656 12.607 3.36094 10.7156C3.43828 8.95781 3.73359 8.00859 3.97969 7.37578C4.30312 6.53906 4.69688 5.93437 5.32266 5.30859C5.95547 4.67578 6.55313 4.28906 7.38984 3.96563C8.02266 3.71953 8.97891 3.42422 10.7297 3.34687C12.6211 3.2625 13.1906 3.24141 18 3.24141ZM18 0C13.1133 0 12.5016 0.0210937 10.582 0.105469C8.66953 0.189844 7.35469 0.499219 6.21563 0.942187C5.02734 1.40625 4.02188 2.01797 3.02344 3.02344C2.01797 4.02187 1.40625 5.02734 0.942188 6.20859C0.499219 7.35469 0.189844 8.6625 0.105469 10.575C0.0210938 12.5016 0 13.1133 0 18C0 22.8867 0.0210938 23.4984 0.105469 25.418C0.189844 27.3305 0.499219 28.6453 0.942188 29.7844C1.40625 30.9727 2.01797 31.9781 3.02344 32.9766C4.02188 33.975 5.02734 34.5937 6.20859 35.0508C7.35469 35.4937 8.6625 35.8031 10.575 35.8875C12.4945 35.9719 13.1062 35.993 17.993 35.993C22.8797 35.993 23.4914 35.9719 25.4109 35.8875C27.3234 35.8031 28.6383 35.4937 29.7773 35.0508C30.9586 34.5937 31.9641 33.975 32.9625 32.9766C33.9609 31.9781 34.5797 30.9727 35.0367 29.7914C35.4797 28.6453 35.7891 27.3375 35.8734 25.425C35.9578 23.5055 35.9789 22.8937 35.9789 18.007C35.9789 13.1203 35.9578 12.5086 35.8734 10.5891C35.7891 8.67656 35.4797 7.36172 35.0367 6.22266C34.5938 5.02734 33.982 4.02187 32.9766 3.02344C31.9781 2.025 30.9727 1.40625 29.7914 0.949219C28.6453 0.50625 27.3375 0.196875 25.425 0.1125C23.4984 0.0210938 22.8867 0 18 0Z" fill="white"/>
                  <path d="M18 8.75391C12.8953 8.75391 8.75391 12.8953 8.75391 18C8.75391 23.1047 12.8953 27.2461 18 27.2461C23.1047 27.2461 27.2461 23.1047 27.2461 18C27.2461 12.8953 23.1047 8.75391 18 8.75391ZM18 23.9977C14.6883 23.9977 12.0023 21.3117 12.0023 18C12.0023 14.6883 14.6883 12.0023 18 12.0023C21.3117 12.0023 23.9977 14.6883 23.9977 18C23.9977 21.3117 21.3117 23.9977 18 23.9977Z" fill="white"/>
                  <path d="M29.7703 8.38821C29.7703 9.58352 28.8 10.5468 27.6117 10.5468C26.4164 10.5468 25.4531 9.57649 25.4531 8.38821C25.4531 7.1929 26.4234 6.22961 27.6117 6.22961C28.8 6.22961 29.7703 7.19993 29.7703 8.38821Z" fill="white"/>
                  </g>
                  <defs>
                  <clipPath id="clip0_1811_399">
                  <rect width="36" height="36" fill="white"/>
                  </clipPath>
                  </defs>
                </svg>
            </div>
          </div>
        </a>

        <div class="sec-SocialNetwork_Pinned">
          <div class="mod-PinnedProducts">
            ${products.map(product => {
              const handle = product.product.url.split('/products/').pop()
              const images = this.state.productImages.get(handle)
              const src = images?.[4]?.src ?? product.product.image_url
              const imageUrl = `${src}${src.includes('?') ? '&' : '?'}width=150`
              return `
              <div class="mod-PinnedProducts_Item">
                <pinned-products-quick-add data-product-handle="${handle}" data-variant-id="">
                  <button
                    type="button"
                    class="mod-PinnedProduct_ItemButton"
                  >
                    <img src="${imageUrl}" alt="${product.product.source}" loading="lazy">
                  </button>
                </pinned-products-quick-add>
              </div>
            `}).join('')}
          </div>
        </div>

      </div>
    `
  }
}

if (!customElements.get('social-network-ticker')) {
  customElements.define('social-network-ticker', SocialNetworkTicker)
}
